import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { pushAPI, notificationAPI } from '../lib/api';
import { toast } from 'sonner';

const PushContext = createContext(null);

export const usePush = () => {
  const context = useContext(PushContext);
  return context;
};

export const PushProvider = ({ children }) => {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isOneSignalReady, setIsOneSignalReady] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Initialize OneSignal and link with user
  const initOneSignal = useCallback(async () => {
    if (!user || !window.OneSignalDeferred) return;

    try {
      window.OneSignalDeferred.push(async function(OneSignal) {
        // Check if already initialized
        const isInitialized = await OneSignal.Notifications.permission;
        setPermission(isInitialized ? 'granted' : 'default');
        setIsOneSignalReady(true);

        // Login user to OneSignal with their user_id as external_id
        // This links their device to their account for targeted notifications
        if (user.id) {
          await OneSignal.login(user.id);
          console.log('OneSignal: User logged in with ID:', user.id);
        }

        // Listen for notification events
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          console.log('OneSignal notification received:', event.notification);
          // Optionally show a toast
          toast(event.notification.title, {
            description: event.notification.body,
          });
        });

        OneSignal.Notifications.addEventListener('click', (event) => {
          console.log('OneSignal notification clicked:', event.notification);
          // Handle navigation based on notification data
          const data = event.notification.additionalData;
          if (data?.url) {
            window.location.href = data.url;
          }
        });
      });
    } catch (error) {
      console.error('OneSignal init error:', error);
    }
  }, [user]);

  // Request notification permission via OneSignal
  const requestPermission = useCallback(async () => {
    if (!window.OneSignalDeferred) {
      console.log('OneSignal not loaded');
      return false;
    }

    return new Promise((resolve) => {
      window.OneSignalDeferred.push(async function(OneSignal) {
        try {
          // Request permission
          await OneSignal.Notifications.requestPermission();
          const granted = await OneSignal.Notifications.permission;
          setPermission(granted ? 'granted' : 'denied');
          
          if (granted) {
            toast.success('Push notifications enabled!');
            resolve(true);
          } else {
            toast.error('Push notifications blocked. Please enable in browser settings.');
            resolve(false);
          }
        } catch (error) {
          console.error('Permission request error:', error);
          resolve(false);
        }
      });
    });
  }, []);

  // Logout from OneSignal (when user logs out)
  const logoutOneSignal = useCallback(async () => {
    if (!window.OneSignalDeferred) return;

    window.OneSignalDeferred.push(async function(OneSignal) {
      await OneSignal.logout();
      console.log('OneSignal: User logged out');
    });
  }, []);

  // Poll for in-app notifications (for notification center)
  const pollNotifications = useCallback(async () => {
    if (!user) return;

    try {
      const response = await notificationAPI.getAll();
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error polling notifications:', error);
    }
  }, [user]);

  // Initialize OneSignal when user logs in
  useEffect(() => {
    if (user) {
      initOneSignal();
    }
  }, [user, initOneSignal]);

  // Poll for notifications periodically
  useEffect(() => {
    if (!user) return;

    // Initial fetch after a short delay
    const initialFetch = setTimeout(() => {
      pollNotifications();
    }, 1000);
    
    const interval = setInterval(pollNotifications, 30000); // Poll every 30 seconds

    return () => {
      clearTimeout(initialFetch);
      clearInterval(interval);
    };
  }, [user, pollNotifications]);

  // Cleanup on user logout
  useEffect(() => {
    if (!user && isOneSignalReady) {
      logoutOneSignal();
    }
  }, [user, isOneSignalReady, logoutOneSignal]);

  return (
    <PushContext.Provider value={{
      permission,
      isOneSignalReady,
      unreadCount,
      requestPermission,
      pollNotifications,
      logoutOneSignal,
    }}>
      {children}
    </PushContext.Provider>
  );
};

export default PushContext;
